import { Elements } from '@stripe/react-stripe-js';
import { loadStripe, type Stripe } from '@stripe/stripe-js';
import { useEffect, useState } from 'react';

const stripePublishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || 'pk_test_51SM63rQja5frJ6pBZKGJTPSefEunxuDzWlqh3gw8PuKimRkXCzGjaOAHYZmYD0SjfSdzVLydBOZ6LXMJ9yKi5fyG003fzRPFrl';

export function StripeProvider({ children }: { children: React.ReactNode }) {
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);

  useEffect(() => {
    setStripePromise(loadStripe(stripePublishableKey));
  }, []);

  if (!stripePromise) {
    return <>{children}</>;
  }

  return (
    <Elements 
      stripe={stripePromise}
      options={{
        appearance: {
          theme: 'night',
          variables: {
            colorPrimary: '#D4AF37',
            colorPrimaryText: '#121212',
            colorBackground: '#121212',
            colorText: '#F5F3EE',
            colorTextSecondary: '#F5F3EE99',
            colorTextPlaceholder: '#F5F3EEB3',
            colorDanger: '#DC2626',
            colorSuccess: '#D4AF37',
            fontFamily: 'Inter, system-ui, sans-serif',
            fontSizeBase: '15px',
            fontWeightNormal: '400',
            fontWeightMedium: '500',
            fontWeightBold: '600',
            borderRadius: '12px',
            spacingUnit: '6px',
            colorIconTab: '#D4AF37',
            colorIconTabSelected: '#D4AF37',
            colorLogo: 'light',
          },
          rules: {
            '.Tab': {
              backgroundColor: '#2A2A2A',
              border: '1px solid rgba(212, 175, 55, 0.4)',
              boxShadow: 'none',
              transition: 'all 0.2s ease',
            },
            '.Tab:hover': {
              backgroundColor: '#2F2F2F',
              border: '1px solid rgba(212, 175, 55, 0.5)',
            },
            '.Tab--selected': {
              backgroundColor: 'rgba(212, 175, 55, 0.2)',
              border: '1px solid rgba(212, 175, 55, 0.6)',
              boxShadow: '0 0 0 2px rgba(212, 175, 55, 0.3)',
            },
            '.Input': {
              backgroundColor: '#2A2A2A',
              border: '1px solid rgba(212, 175, 55, 0.4)',
              boxShadow: 'none',
              padding: '12px',
              fontSize: '15px',
              transition: 'all 0.2s ease',
            },
            '.Input:hover': {
              border: '1px solid rgba(212, 175, 55, 0.5)',
            },
            '.Input:focus': {
              backgroundColor: '#2A2A2A',
              border: '1px solid rgba(212, 175, 55, 0.7)',
              boxShadow: '0 0 0 3px rgba(212, 175, 55, 0.2)',
              outline: 'none',
            },
            '.Label': {
              color: '#F5F3EE',
              fontSize: '13px',
              fontWeight: '500',
              marginBottom: '6px',
              letterSpacing: '0.3px',
            },
            '.TabLabel': {
              color: '#F5F3EE',
            },
            '.TabIcon': {
              color: '#D4AF37',
              backgroundColor: 'transparent',
              border: 'none',
            },
            '.TabIcon--selected': {
              color: '#D4AF37',
              backgroundColor: 'transparent',
            },
            '.Link': {
              color: '#D4AF37',
            },
            '.Block': {
              backgroundColor: '#1A1A1A',
              border: '1px solid rgba(212, 175, 55, 0.2)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.25)'
            },
            '.Error': {
              color: '#EF4444',
              fontSize: '13px',
            },
            '.PickerItem': {
              backgroundColor: '#2A2A2A',
              border: '1px solid rgba(212, 175, 55, 0.4)',
              padding: '12px',
            },
            '.PickerItem:hover': {
              backgroundColor: '#2F2F2F',
              border: '1px solid rgba(212, 175, 55, 0.5)',
            },
            '.PickerItem--selected': {
              backgroundColor: 'rgba(212, 175, 55, 0.25)',
              border: '1px solid rgba(212, 175, 55, 0.7)',
            },
            '.Wallets': {
              backgroundColor: '#1A1A1A',
              border: '1px solid rgba(212, 175, 55, 0.2)',
            },
            '.WalletTab': {
              backgroundColor: '#2A2A2A',
              border: '1px solid rgba(212, 175, 55, 0.4)',
            },
            '.WalletTab:hover': {
              backgroundColor: '#2F2F2F',
              border: '1px solid rgba(212, 175, 55, 0.5)',
            },
            '.WalletTab--selected': {
              backgroundColor: 'rgba(212, 175, 55, 0.2)',
              border: '1px solid rgba(212, 175, 55, 0.6)',
            },
            '.RedirectText': {
              color: '#F5F3EE',
            },
          },
        },
      }}
    >
      {children}
    </Elements>
  );
}
